import { Component } from "react"
import PropTypes from "prop-types"
import LimitedTextInput from "shared/limited_text_input"
import InlineErrors from "shared/components/inline_errors"
import compact from "lodash/compact"
import uniq from "lodash/uniq"
import { arrayAdd, arrayRemove } from "shared/lib/utils"
import classnames from "classnames"
import XIcon from "shared/components/icons/encircled_x_icon"
import styles from "./applet_metadata.module.scss"

class AppletMetadata extends Component {
  state = { selectedTag: null }

  onSelectTag = tagId => {
    const tag = this.props.tagOptions.find(t => t.id === tagId)
    this.setState({ selectedTag: tag })
  }

  onAddTag = () => {
    const { tags } = this.props.applet
    if (tags.length === 5 || tags.find(t => t.id === this.state.selectedTag.id)) return

    this.handleChange("tags", arrayAdd(tags, this.state.selectedTag))
  }

  handleChange(key, value) {
    this.props.update({ [key]: value })
  }

  potentialPrimaryServices() {
    const { applet, services } = this.props

    const moduleNames = compact(
      uniq([
        applet.applet_trigger.channel_module_name,
        ...applet.applet_queries.map(aq => aq.channel_module_name),
        ...applet.applet_actions.map(aa => aa.channel_module_name),
      ])
    )

    return moduleNames.map(name => services.byModuleName[name])
  }

  renderPrimaryServiceField() {
    const { applet, errors } = this.props

    const services = this.potentialPrimaryServices()
    const primaryService = services.find(s => s.id === applet.channel_id)
    const fieldErrors = errors.getAll("/channel_id")

    return (
      <div className="field">
        <div className="label">Applet service</div>

        <InlineErrors errors={fieldErrors} />

        <div className="applet-composer-primary-service-input">
          {primaryService ? (
            <img
              className="applet-composer-primary-service-logo"
              src={primaryService.lrg_monochrome_image_url}
              alt={primaryService.name}
              title={primaryService.name}
              style={{ backgroundColor: `#${primaryService.brand_color}` }}
            />
          ) : (
            <div className="applet-composer-primary-service-logo" />
          )}

          <div className="input">
            <select
              disabled={!services.length}
              onChange={e => this.handleChange("channel_id", e.target.value)}
              value={applet.channel_id}
            >
              {services.length ? (
                services.map(service => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))
              ) : (
                <option>Add a trigger, query, or action above</option>
              )}
            </select>

            <p className="note">The service logo and color that will appear on the Applet card.</p>
          </div>
        </div>
      </div>
    )
  }

  render() {
    const { applet, errors, showPrimaryService, canTag, tagOptions } = this.props

    return (
      <section>
        <div className="field">
          <div className="label">Applet title</div>
          <div className="input">
            <LimitedTextInput
              required
              value={applet.name}
              onChange={this.handleChange.bind(this, "name")}
              limit={100}
              multiline={false}
              counterPosition="top"
              hardLimit
              errors={errors.getAll("/name")}
            />
            <p className="note">Use sentence case capitalization without a period.</p>
          </div>
        </div>

        <div className="field">
          <div className="label">Applet description</div>
          <div className="input">
            <LimitedTextInput
              required
              onChange={description => {
                if (description.length < 1) {
                  document.dispatchEvent(new Event("applet_composer_description_empty"))
                } else {
                  document.dispatchEvent(new Event("applet_composer_description_filled"))
                }
                this.handleChange("description", description)
              }}
              value={applet.description}
              limit={500}
              counterPosition="top"
              hardLimit
              minRows={3}
              errors={errors.getAll("/description")}
            />

            <p className="note">
              Complement your title with more details about what your Applet will do and why someone should turn it on.
            </p>
          </div>
        </div>

        {canTag && (
          <div className="field">
            <div className="label">Applet tags</div>
            <div className="input">
              <span className={styles.select}>
                <select onChange={e => this.onSelectTag(e.target.value)}>
                  <option key={0} disabled selected>
                    {"Select a tag"}
                  </option>
                  {tagOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
                <button className="button button-tertiary" onClick={this.onAddTag} disabled={applet.tags.length === 5}>
                  Add tag
                </button>
              </span>
              <div>
                {applet.tags.map(tag => (
                  <span key={tag.id} className={styles.tag}>
                    {tag.name}
                    <span
                      onClick={() =>
                        this.handleChange(
                          "tags",
                          arrayRemove(
                            applet.tags,
                            applet.tags.findIndex(t => t.id === tag.id)
                          )
                        )
                      }
                    >
                      <XIcon width="18" height="18" />
                    </span>
                  </span>
                ))}
                <p className={classnames("note", { [styles.limit]: applet.tags.length === 5 })}>
                  {applet.tags.length === 5 ? "Limit of 5 tags achieved." : "Add up to 5 tags."}
                </p>
              </div>
            </div>
          </div>
        )}

        {showPrimaryService && this.renderPrimaryServiceField()}
      </section>
    )
  }
}

AppletMetadata.propTypes = {
  applet: PropTypes.object.isRequired,
  errors: PropTypes.object,
  services: PropTypes.object.isRequired,
  showPrimaryService: PropTypes.bool.isRequired,
  update: PropTypes.func.isRequired,
  canTag: PropTypes.bool.isRequired,
  tagOptions: PropTypes.array.isRequired,
}

export default AppletMetadata
